import { useState, useCallback } from 'react';
import TalentosAPI from '../../../api/talentos';
import { useApp } from '../../../context/AppContext';
import logger from '../../../utils/logger';
import { useCreditCodes } from './useCreditCodes';

/**
 * Hook para gestionar la integración con la API de Talentos
 */
export const useTalentos = () => {
    const { notify } = useApp();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    // Hook para códigos de crédito
    const creditCodesHook = useCreditCodes();

    const handleError = useCallback((err, defaultMessage = 'Error en la operación') => {
        const errorMessage = err?.response?.data?.error || err?.message || defaultMessage;
        logger.error('Error en Talentos API:', err);
        setError(errorMessage);
        notify({
            type: 'error',
            title: 'Error',
            text: errorMessage
        });
        throw err;
    }, [notify]);

    const handleSuccess = useCallback((message = 'Operación exitosa') => {
        setError(null);
        notify({
            type: 'success',
            title: 'Éxito',
            text: message
        });
    }, [notify]);

    // ==================== USUARIOS ====================

    const getUsers = useCallback(async (filters = {}) => {
        setIsLoading(true);
        setError(null);
        try {
            const data = await TalentosAPI.getUsers(filters);
            return data;
        } catch (err) {
            handleError(err, 'Error al obtener usuarios');
            return [];
        } finally {
            setIsLoading(false);
        }
    }, [handleError]);

    const getUserById = useCallback(async (userId) => {
        setIsLoading(true);
        setError(null);
        try {
            const data = await TalentosAPI.getUserById(userId);
            return data;
        } catch (err) {
            handleError(err, 'Error al obtener usuario');
            return null;
        } finally {
            setIsLoading(false);
        }
    }, [handleError]);

    const getPostulants = useCallback(async (filters = {}) => {
        setIsLoading(true);
        setError(null);
        try {
            const data = await TalentosAPI.getPostulants(filters);
            return data;
        } catch (err) {
            handleError(err, 'Error al obtener postulantes');
            return [];
        } finally {
            setIsLoading(false);
        }
    }, [handleError]);

    const getEmployers = useCallback(async (filters = {}) => {
        setIsLoading(true);
        setError(null);
        try {
            const data = await TalentosAPI.getEmployers(filters);
            return data;
        } catch (err) {
            handleError(err, 'Error al obtener empleadores');
            return [];
        } finally {
            setIsLoading(false);
        }
    }, [handleError]);

    const getCompanies = useCallback(async (filters = {}) => {
        setIsLoading(true);
        setError(null);
        try {
            const data = await TalentosAPI.getCompanies(filters);
            return data;
        } catch (err) {
            handleError(err, 'Error al obtener compañías');
            return [];
        } finally {
            setIsLoading(false);
        }
    }, [handleError]);

    const registerPostulant = useCallback(async (data) => {
        setIsLoading(true);
        setError(null);
        try {
            const result = await TalentosAPI.registerPostulant(data);
            handleSuccess('Postulante registrado exitosamente');
            return result;
        } catch (err) {
            handleError(err, 'Error al registrar postulante');
            return null;
        } finally {
            setIsLoading(false);
        }
    }, [handleError, handleSuccess]);

    const registerEmployer = useCallback(async (data) => {
        setIsLoading(true);
        setError(null);
        try {
            const result = await TalentosAPI.registerEmployer(data);
            handleSuccess('Empleador registrado exitosamente');
            return result;
        } catch (err) {
            handleError(err, 'Error al registrar empleador');
            return null;
        } finally {
            setIsLoading(false);
        }
    }, [handleError, handleSuccess]);

    // ==================== TRABAJOS ====================

    const getJobs = useCallback(async (filters = {}) => {
        setIsLoading(true);
        setError(null);
        try {
            const data = await TalentosAPI.getJobs(filters);
            logger.info('Jobs data received:', data);
            // Si la respuesta es un objeto con data, extraer el array
            if (data && typeof data === 'object' && !Array.isArray(data)) {
                if (Array.isArray(data.data)) {
                    return data.data;
                }
                if (Array.isArray(data.jobs)) {
                    return data.jobs;
                }
            }
            return Array.isArray(data) ? data : [];
        } catch (err) {
            logger.error('Error getting jobs:', err);
            const errorMessage = err?.response?.data?.error || err?.message || 'Error al obtener trabajos';
            setError(errorMessage);
            notify({
                type: 'error',
                title: 'Error',
                text: errorMessage
            });
            return [];
        } finally {
            setIsLoading(false);
        }
    }, [notify]);

    const getJobById = useCallback(async (jobId) => {
        setIsLoading(true);
        setError(null);
        try {
            const data = await TalentosAPI.getJobById(jobId);
            return data;
        } catch (err) {
            handleError(err, 'Error al obtener trabajo');
            return null;
        } finally {
            setIsLoading(false);
        }
    }, [handleError]);

    const createJob = useCallback(async (data) => {
        setIsLoading(true);
        setError(null);
        try {
            const result = await TalentosAPI.createJob(data);
            handleSuccess('Trabajo creado exitosamente');
            return result;
        } catch (err) {
            handleError(err, 'Error al crear trabajo');
            return null;
        } finally {
            setIsLoading(false);
        }
    }, [handleError, handleSuccess]);

    const updateJob = useCallback(async (jobId, data) => {
        setIsLoading(true);
        setError(null);
        try {
            const result = await TalentosAPI.updateJob(jobId, data);
            handleSuccess('Trabajo actualizado exitosamente');
            return result;
        } catch (err) {
            handleError(err, 'Error al actualizar trabajo');
            return null;
        } finally {
            setIsLoading(false);
        }
    }, [handleError, handleSuccess]);

    const deleteJob = useCallback(async (jobId) => {
        setIsLoading(true);
        setError(null);
        try {
            const result = await TalentosAPI.deleteJob(jobId);
            handleSuccess('Trabajo eliminado exitosamente');
            return result;
        } catch (err) {
            handleError(err, 'Error al eliminar trabajo');
            return null;
        } finally {
            setIsLoading(false);
        }
    }, [handleError, handleSuccess]);

    // ==================== PRÁCTICAS ====================

    const getPractices = useCallback(async (filters = {}) => {
        setIsLoading(true);
        setError(null);
        try {
            const data = await TalentosAPI.getPractices(filters);
            logger.info('Practices data received:', data);
            // Si la respuesta es un objeto con data, extraer el array
            if (data && typeof data === 'object' && !Array.isArray(data)) {
                if (Array.isArray(data.data)) {
                    return data.data;
                }
                if (Array.isArray(data.practices)) {
                    return data.practices;
                }
            }
            return Array.isArray(data) ? data : [];
        } catch (err) {
            logger.error('Error getting practices:', err);
            const errorMessage = err?.response?.data?.error || err?.message || 'Error al obtener prácticas';
            setError(errorMessage);
            notify({
                type: 'error',
                title: 'Error',
                text: errorMessage
            });
            return [];
        } finally {
            setIsLoading(false);
        }
    }, [notify]);

    const getPracticeById = useCallback(async (practiceId) => {
        setIsLoading(true);
        setError(null);
        try {
            const data = await TalentosAPI.getPracticeById(practiceId);
            return data;
        } catch (err) {
            handleError(err, 'Error al obtener práctica');
            return null;
        } finally {
            setIsLoading(false);
        }
    }, [handleError]);

    const getPracticeDetails = useCallback(async (practiceId) => {
        setIsLoading(true);
        setError(null);
        try {
            const data = await TalentosAPI.getPracticeDetails(practiceId);
            return data;
        } catch (err) {
            handleError(err, 'Error al obtener detalles de práctica');
            return null;
        } finally {
            setIsLoading(false);
        }
    }, [handleError]);

    const createPractice = useCallback(async (data) => {
        setIsLoading(true);
        setError(null);
        try {
            const result = await TalentosAPI.createPractice(data);
            handleSuccess('Práctica creada exitosamente');
            return result;
        } catch (err) {
            handleError(err, 'Error al crear práctica');
            return null;
        } finally {
            setIsLoading(false);
        }
    }, [handleError, handleSuccess]);

    const updatePractice = useCallback(async (practiceId, data) => {
        setIsLoading(true);
        setError(null);
        try {
            const result = await TalentosAPI.updatePractice(practiceId, data);
            handleSuccess('Práctica actualizada exitosamente');
            return result;
        } catch (err) {
            handleError(err, 'Error al actualizar práctica');
            return null;
        } finally {
            setIsLoading(false);
        }
    }, [handleError, handleSuccess]);

    const deletePractice = useCallback(async (practiceId) => {
        setIsLoading(true);
        setError(null);
        try {
            const result = await TalentosAPI.deletePractice(practiceId);
            handleSuccess('Práctica eliminada exitosamente');
            return result;
        } catch (err) {
            handleError(err, 'Error al eliminar práctica');
            return null;
        } finally {
            setIsLoading(false);
        }
    }, [handleError, handleSuccess]);

    // ==================== POSTULACIONES ====================

    const getApplications = useCallback(async (filters = {}) => {
        setIsLoading(true);
        setError(null);
        try {
            const data = await TalentosAPI.getApplications(filters);
            logger.info('Applications data received:', data);
            // Si la respuesta es un objeto con data, extraer el array
            if (data && typeof data === 'object' && !Array.isArray(data)) {
                if (Array.isArray(data.data)) {
                    return data.data;
                }
                if (Array.isArray(data.applications)) {
                    return data.applications;
                }
            }
            return Array.isArray(data) ? data : [];
        } catch (err) {
            logger.error('Error getting applications:', err);
            const errorMessage = err?.response?.data?.error || err?.message || 'Error al obtener postulaciones';
            setError(errorMessage);
            notify({
                type: 'error',
                title: 'Error',
                text: errorMessage
            });
            return [];
        } finally {
            setIsLoading(false);
        }
    }, [notify]);

    const getApplicationById = useCallback(async (applicationId) => {
        setIsLoading(true);
        setError(null);
        try {
            const data = await TalentosAPI.getApplicationById(applicationId);
            return data;
        } catch (err) {
            handleError(err, 'Error al obtener postulación');
            return null;
        } finally {
            setIsLoading(false);
        }
    }, [handleError]);

    const createApplication = useCallback(async (data) => {
        setIsLoading(true);
        setError(null);
        try {
            const result = await TalentosAPI.createApplication(data);
            handleSuccess('Postulación creada exitosamente');
            return result;
        } catch (err) {
            handleError(err, 'Error al crear postulación');
            return null;
        } finally {
            setIsLoading(false);
        }
    }, [handleError, handleSuccess]);

    const updateApplicationStage = useCallback(async (applicationId, newStage) => {
        setIsLoading(true);
        setError(null);
        try {
            const result = await TalentosAPI.updateApplicationStage(applicationId, newStage);
            handleSuccess('Etapa de postulación actualizada exitosamente');
            return result;
        } catch (err) {
            handleError(err, 'Error al actualizar etapa');
            return null;
        } finally {
            setIsLoading(false);
        }
    }, [handleError, handleSuccess]);

    const updateApplicationStatus = useCallback(async (applicationId, status) => {
        setIsLoading(true);
        setError(null);
        try {
            const result = await TalentosAPI.updateApplicationStatus(applicationId, status);
            handleSuccess('Estado de postulación actualizado exitosamente');
            return result;
        } catch (err) {
            handleError(err, 'Error al actualizar estado');
            return null;
        } finally {
            setIsLoading(false);
        }
    }, [handleError, handleSuccess]);

    // ==================== WEBHOOKS ====================

    const getWebhooks = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const data = await TalentosAPI.getWebhooks();
            return data;
        } catch (err) {
            handleError(err, 'Error al obtener webhooks');
            return [];
        } finally {
            setIsLoading(false);
        }
    }, [handleError]);

    const registerWebhook = useCallback(async (data) => {
        setIsLoading(true);
        setError(null);
        try {
            const result = await TalentosAPI.registerWebhook(data);
            handleSuccess('Webhook registrado exitosamente');
            return result;
        } catch (err) {
            handleError(err, 'Error al registrar webhook');
            return null;
        } finally {
            setIsLoading(false);
        }
    }, [handleError, handleSuccess]);

    // ==================== DASHBOARD Y KPIs ====================

    const getKPIs = useCallback(async (filters = {}) => {
        setIsLoading(true);
        setError(null);
        try {
            const data = await TalentosAPI.getKPIs(filters);
            logger.info('KPIs data received:', data);
            return data;
        } catch (err) {
            logger.error('Error getting KPIs:', err);
            const errorMessage = err?.response?.data?.error || err?.message || 'Error al obtener KPIs';
            setError(errorMessage);
            notify({
                type: 'error',
                title: 'Error',
                text: errorMessage
            });
            return null;
        } finally {
            setIsLoading(false);
        }
    }, [notify]);

    const getGrowthStatistics = useCallback(async (period = 'month', filters = {}) => {
        setIsLoading(true);
        setError(null);
        try {
            const data = await TalentosAPI.getGrowthStatistics(period, filters);
            return data;
        } catch (err) {
            logger.error('Error getting growth statistics:', err);
            const errorMessage = err?.response?.data?.error || err?.message || 'Error al obtener estadísticas de crecimiento';
            setError(errorMessage);
            notify({
                type: 'error',
                title: 'Error',
                text: errorMessage
            });
            return null;
        } finally {
            setIsLoading(false);
        }
    }, [notify]);

    const getCategoryStatistics = useCallback(async (filters = {}) => {
        setIsLoading(true);
        setError(null);
        try {
            const data = await TalentosAPI.getCategoryStatistics(filters);
            return data;
        } catch (err) {
            logger.error('Error getting category statistics:', err);
            const errorMessage = err?.response?.data?.error || err?.message || 'Error al obtener estadísticas por categoría';
            setError(errorMessage);
            notify({
                type: 'error',
                title: 'Error',
                text: errorMessage
            });
            return null;
        } finally {
            setIsLoading(false);
        }
    }, [notify]);

    const getGeographicStatistics = useCallback(async (filters = {}) => {
        setIsLoading(true);
        setError(null);
        try {
            const data = await TalentosAPI.getGeographicStatistics(filters);
            return data;
        } catch (err) {
            logger.error('Error getting geographic statistics:', err);
            const errorMessage = err?.response?.data?.error || err?.message || 'Error al obtener estadísticas geográficas';
            setError(errorMessage);
            notify({
                type: 'error',
                title: 'Error',
                text: errorMessage
            });
            return null;
        } finally {
            setIsLoading(false);
        }
    }, [notify]);

    const getApplicationStatistics = useCallback(async (filters = {}) => {
        setIsLoading(true);
        setError(null);
        try {
            const data = await TalentosAPI.getApplicationStatistics(filters);
            return data;
        } catch (err) {
            logger.error('Error getting application statistics:', err);
            const errorMessage = err?.response?.data?.error || err?.message || 'Error al obtener estadísticas de postulaciones';
            setError(errorMessage);
            notify({
                type: 'error',
                title: 'Error',
                text: errorMessage
            });
            return null;
        } finally {
            setIsLoading(false);
        }
    }, [notify]);

    const getPaymentStatistics = useCallback(async (filters = {}) => {
        setIsLoading(true);
        setError(null);
        try {
            const data = await TalentosAPI.getPaymentStatistics(filters);
            return data;
        } catch (err) {
            logger.error('Error getting payment statistics:', err);
            const errorMessage = err?.response?.data?.error || err?.message || 'Error al obtener estadísticas de pagos';
            setError(errorMessage);
            notify({
                type: 'error',
                title: 'Error',
                text: errorMessage
            });
            return null;
        } finally {
            setIsLoading(false);
        }
    }, [notify]);

    const getDashboardSummary = useCallback(async (filters = {}) => {
        setIsLoading(true);
        setError(null);
        try {
            const data = await TalentosAPI.getDashboardSummary(filters);
            logger.info('Dashboard summary received:', data);
            return data;
        } catch (err) {
            logger.error('Error getting dashboard summary:', err);
            const errorMessage = err?.response?.data?.error || err?.message || 'Error al obtener resumen del dashboard';
            setError(errorMessage);
            notify({
                type: 'error',
                title: 'Error',
                text: errorMessage
            });
            return null;
        } finally {
            setIsLoading(false);
        }
    }, [notify]);

    return {
        // Estado
        isLoading,
        error,

        // Usuarios
        getUsers,
        getUserById,
        getPostulants,
        getEmployers,
        getCompanies,
        registerPostulant,
        registerEmployer,

        // Trabajos
        getJobs,
        getJobById,
        createJob,
        updateJob,
        deleteJob,

        // Prácticas
        getPractices,
        getPracticeById,
        getPracticeDetails,
        createPractice,
        updatePractice,
        deletePractice,

        // Postulaciones
        getApplications,
        getApplicationById,
        createApplication,
        updateApplicationStage,
        updateApplicationStatus,

        // Webhooks
        getWebhooks,
        registerWebhook,

        // Dashboard y KPIs
        getKPIs,
        getGrowthStatistics,
        getCategoryStatistics,
        getGeographicStatistics,
        getApplicationStatistics,
        getPaymentStatistics,
        getDashboardSummary,

        // Códigos de Crédito
        creditCodes: {
            // Estado
            creditCodes: creditCodesHook.codes,
            currentCreditCode: creditCodesHook.currentCode,
            creditStats: creditCodesHook.stats,
            creditEligibility: creditCodesHook.eligibility,

            // Generación
            generateCreditCode: creditCodesHook.generateCode,

            // Gestión administrativa
            getCreditCodes: creditCodesHook.getAllCodes,
            getCreditCodeById: creditCodesHook.getCodeById,
            getEmployerCreditCodes: creditCodesHook.getEmployerCodes,
            updateCreditCode: creditCodesHook.updateCode,
            deleteCreditCode: creditCodesHook.deleteCode,
            expireCreditCode: creditCodesHook.expireCode,

            // Reclamo
            claimCreditCode: creditCodesHook.claimCode,

            // Consultas
            checkCreditEligibility: creditCodesHook.checkEligibility,
            getCreditStats: creditCodesHook.getStats,
            getStatisticsByCreator: creditCodesHook.getStatisticsByCreator,
            diagnoseCreditConnectivity: creditCodesHook.diagnoseConnectivity,
            testCreditConnection: creditCodesHook.testConnection,
            runCreditDiagnostics: creditCodesHook.runDiagnostics,
            runCreditFullAudit: creditCodesHook.runFullAudit,

            // Desarrollo/Debugging
            clearCreditTokens: creditCodesHook.clearTokens,
            toggleCreditDevMode: creditCodesHook.toggleDevMode,
            isCreditDevMode: creditCodesHook.isDevMode,

            // Utilidades
            copyCreditCodeToClipboard: creditCodesHook.copyToClipboard,

            // UI Helpers
            getCreditTableColumns: creditCodesHook.getTableColumns,
            getCreditTableCustomElements: creditCodesHook.getTableCustomElements
        },

        // API directa (para casos especiales)
        api: TalentosAPI
    };
};

